<template>
  <SatellitesPage>
    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <div v-else-if="!domains.length" class="sat-empty">
      <span class="sat-empty__title">{{ $t("satellites.links.no_domains_title") }}</span>
      <span class="sat-empty__text">{{ $t("satellites.links.no_domains_text") }}</span>
      <router-link :to="{ name: 'SatelliteDomains', query: { add: '1' } }" class="btn _bg-green sat-btn-inline"><SatIcon name="plus" /> {{ $t("satellites.domains.add_button") }}</router-link>
    </div>

    <div v-else class="links-layout">
      <!-- Builder -->
      <section class="sat-card links-builder">
        <h2 class="sat-card__title"><SatIcon name="link" :size="18" /> {{ $t("satellites.links.title") }}</h2>
        <p class="sat-card__subtitle">{{ $t("satellites.links.subtitle") }}</p>

        <div class="links-step">
          <span class="links-step__num">1</span>
          <div class="links-step__body">
            <div class="sat-field__label">{{ $t("satellites.links.domain") }}</div>
            <SatPopover :width="400" class="links-domain-popover">
              <template #trigger>
                <button type="button" class="links-select">
                  <template v-if="domain">
                    <span class="links-select__main">{{ domain.host }}</span>
                    <span class="links-select__meta">{{ domain.router ? domain.router.name : $t("satellites.domains.no_router") }}</span>
                  </template>
                  <span v-else class="links-select__placeholder">{{ $t("satellites.links.pick_domain") }}</span>
                  <SatIcon name="chevron" :size="16" class="links-select__chevron" />
                </button>
              </template>
              <template #default="{ close }">
                <div class="sat-menu">
                  <div v-if="pickable.length > 7" class="links-search">
                    <SatIcon name="search" :size="14" />
                    <input v-model.trim="domainQuery" type="text" :placeholder="$t('satellites.domains.search')" />
                  </div>
                  <button v-for="item in filteredDomains" :key="item.id" type="button" class="sat-menu__item" :class="{ _active: item.id === domainId }" @click="close(), pickDomain(item.id)">
                    <span class="sat-menu__check"><SatIcon v-if="item.id === domainId" name="check" :size="14" /></span>
                    <span class="links-option__host">{{ item.host }}</span>
                    <span class="sat-menu__meta" :class="{ 'links-option__warn': !item.router }">{{ item.router ? item.router.name : $t("satellites.domains.no_router") }}</span>
                  </button>
                  <div v-if="!filteredDomains.length" class="links-option__empty">{{ $t("satellites.domains.nothing_found") }}</div>
                </div>
              </template>
            </SatPopover>
            <div v-if="scopeRouter" class="links-scope">
              <span class="sat-chip _accent">
                {{ $t("satellites.links.router_scope", { name: scopeRouter.name }) }}
                <button type="button" :title="$t('satellites.domains.clear_filter')" @click="clearScope"><SatIcon name="x" :size="12" /></button>
              </span>
            </div>
          </div>
        </div>

        <template v-if="traffic && traffic.router">
          <div class="links-step">
            <span class="links-step__num">2</span>
            <div class="links-step__body">
              <div class="sat-field__label">{{ $t("satellites.links.route") }}</div>
              <div class="links-routes">
                <button v-for="item in traffic.router.routes" :key="item.path" type="button" class="links-route" :class="{ _active: route && item.path === route.path }" @click="pickRoute(item.path)">
                  <span class="sat-mono">{{ item.path }}</span>
                  <span class="links-route__meta">{{ pl("satellites.counts.landings", item.landings.length) }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="route" class="links-step">
            <span class="links-step__num">3</span>
            <div class="links-step__body">
              <div class="sat-field__label">{{ $t("satellites.links.platform") }}</div>
              <template v-if="route.platform.mode === 'param'">
                <custom-multiselect v-model="platformOption" :list="platformOptions" search-by="title" :allowEmpty="false" class="links-platform">
                  <template v-slot:block="props">
                    <span :class="{ 'sat-muted': !props.value.id }">{{ props.value.title }}</span>
                  </template>
                  <template v-slot:list="props">
                    <span>{{ props.value.title }}</span>
                  </template>
                </custom-multiselect>
                <div class="sat-field__hint">{{ $t("satellites.links.platform_param", { param: route.platform.param }) }}</div>
              </template>
              <div v-else class="links-fixed">
                <SatIcon name="lock" :size="14" />
                <span>{{ route.platform.name || `#${route.platform.id}` }}</span>
                <span class="sat-muted">{{ $t("satellites.links.platform_fixed") }}</span>
              </div>
            </div>
          </div>

          <div v-if="route && enabledSubs.length" class="links-step">
            <span class="links-step__num">4</span>
            <div class="links-step__body">
              <div class="sat-field__label">{{ $t("satellites.links.subs") }} <span class="sat-muted links-optional">{{ $t("satellites.links.optional") }}</span></div>
              <div class="links-subs">
                <label v-for="sub in enabledSubs" :key="sub.index" class="links-sub">
                  <span class="links-sub__label">sub{{ sub.index + 1 }} <span class="sat-mono">{{ sub.name }}</span></span>
                  <input v-model.trim="subValues[sub.index]" type="text" :placeholder="$t('satellites.links.sub_placeholder', { macro: '{clickid}' })" />
                </label>
              </div>
              <div class="sat-field__hint">{{ $t("satellites.links.macros_hint", { macro: "{clickid}" }) }}</div>
            </div>
          </div>

          <!-- Result -->
          <div v-if="route" class="links-result">
            <div class="links-result__url" :class="{ _incomplete: !complete }">
              <span v-for="(part, index) in parts" :key="index" :class="`_${part.kind}`">{{ part.text }}</span>
            </div>
            <div class="links-result__actions">
              <button type="button" class="btn _bg-green sat-btn-inline links-copy" :disabled="!complete" @click="copy">
                <SatIcon :name="copied ? 'check' : 'copy'" />
                {{ copied ? $t("satellites.links.copied") : $t("satellites.links.copy") }}
              </button>
              <a :href="complete ? url : null" target="_blank" rel="noopener noreferrer" class="btn _border _green sat-btn-inline" :class="{ disabled: !complete }">
                <SatIcon name="external" /> {{ $t("satellites.links.open_link") }}
              </a>
            </div>
            <div v-if="!complete" class="sat-field__hint">{{ $t("satellites.links.pick_platform_first") }}</div>
          </div>
        </template>

        <div v-else-if="traffic && !traffic.router" class="sat-note _warn mt-3">
          <SatIcon name="alert" />
          <span>
            {{ $t("satellites.links.no_router_note") }}
            <router-link :to="{ name: 'SatelliteDomains' }" class="sat-link">{{ $t("satellites.links.assign_router") }}</router-link>
          </span>
        </div>

        <div v-if="trafficLoading" class="d-flex justify-content-center py-4">
          <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
        </div>
      </section>

      <!-- Where the traffic goes -->
      <aside v-if="traffic" class="links-side">
        <section class="links-verdict" :class="`_${check.level}`">
          <SatIcon :name="check.level === 'ok' ? 'check' : 'alert'" :size="22" class="links-verdict__icon" />
          <div>
            <div class="links-verdict__title">{{ verdictTitle }}</div>
            <ul v-if="visibleIssues.length" class="links-verdict__issues">
              <li v-for="issue in visibleIssues" :key="issue.key" :class="`_${issue.level}`">{{ $t(`satellites.links.issues.${issue.key}`, issue.params) }}</li>
            </ul>
          </div>
          <button type="button" class="sat-icon-btn links-verdict__refresh" :title="$t('satellites.links.recheck')" :disabled="trafficLoading" @click="loadTraffic"><SatIcon name="refresh" :size="15" /></button>
        </section>

        <section v-if="route" class="sat-card">
          <h3 class="links-side__title">{{ $t("satellites.links.landings_title") }}</h3>
          <p class="links-side__hint">{{ $t(`satellites.routers.strategies_hint.${route.strategy}`) }}</p>
          <div v-for="landing in route.landings" :key="landing.id" class="links-landing">
            <div class="links-landing__row">
              <span class="links-landing__name">{{ landing.name }}</span>
              <span class="links-landing__share">{{ share(route) }}%</span>
            </div>
            <div class="links-landing__bar"><span :style="{ width: `${share(route)}%` }"></span></div>
          </div>
          <div v-if="!route.landings.length" class="sat-muted">{{ $t("satellites.links.no_landings") }}</div>
        </section>

        <section class="sat-card">
          <h3 class="links-side__title">{{ $t("satellites.links.satellites_title") }}</h3>
          <p class="links-side__hint">{{ placementText }}</p>
          <div v-for="satellite in traffic.satellites" :key="satellite.id" class="links-sat" :class="{ _idle: !check.receiving.includes(satellite.id) }">
            <span class="sat-dot" :class="statusDot(satellite.status)"></span>
            <router-link :to="{ name: 'Satellite', params: { id: satellite.id } }" class="links-sat__name">{{ satName(satellite) }}</router-link>
            <span class="links-sat__ip sat-mono"><CopyIp v-if="satellite.ip" :ip="satellite.ip" /><template v-else>—</template></span>
            <span v-if="dnsKnown" class="sat-chip" :class="satellite.in_dns ? '_ok' : ''">{{ satellite.in_dns ? $t("satellites.links.in_dns") : $t("satellites.links.not_in_dns") }}</span>
          </div>
          <div v-if="!traffic.satellites.length" class="sat-muted">{{ $t("satellites.links.no_satellites") }}</div>
        </section>

        <section class="sat-card">
          <h3 class="links-side__title">{{ $t("satellites.links.dns_title", { host: traffic.domain.host }) }}</h3>
          <div v-if="!traffic.dns.resolved" class="sat-muted">{{ $t("satellites.links.dns_failed") }}</div>
          <div v-else-if="!traffic.dns.records.length" class="sat-muted">{{ $t("satellites.links.dns_empty") }}</div>
          <div v-for="record in traffic.dns.records" :key="record.ip" class="links-dns">
            <span class="sat-mono">{{ record.ip }}</span>
            <SatIcon name="chevron-right" :size="13" class="sat-muted" />
            <span v-if="record.satellite" :class="record.serves ? 'links-dns__ok' : 'links-dns__bad'">{{ satName(record.satellite) }}</span>
            <span v-else class="sat-muted">{{ $t("satellites.links.dns_other") }}</span>
          </div>
          <div class="links-ssl" :class="{ _ok: !!traffic.domain.certificate }">
            <SatIcon name="lock" :size="13" />
            {{ traffic.domain.certificate ? $t("satellites.domains.ssl_until", { date: formatDay(traffic.domain.certificate.not_after) }) : $t("satellites.domains.ssl_on_demand") }}
          </div>
        </section>
      </aside>
    </div>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import CopyIp from "@/components/Satellites/CopyIp.vue";
import SatPopover from "@/components/Satellites/SatPopover.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk } from "@/components/Satellites/format";
import { checkTraffic, linkParts } from "@/components/Satellites/trafficCheck";

export default {
  name: "SatelliteLinks",
  components: { CopyIp, SatellitesPage, SatIcon, SatPopover },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      domains: [],
      platforms: [],
      domainId: null,
      domainQuery: "",
      traffic: null,
      trafficLoading: false,
      routePath: null,
      platformId: null,
      subValues: ["", "", "", ""],
      copied: false,
    };
  },
  computed: {
    // ?router= limits the domains to those of a router (the link action of the routers page)
    scopeRouter() {
      const id = this.$route.query.router;
      const domain = id && this.domains.find((d) => d.router && d.router.id === id);
      return domain ? domain.router : null;
    },
    pickable() {
      return this.scopeRouter ? this.domains.filter((d) => d.router && d.router.id === this.scopeRouter.id) : this.domains;
    },
    filteredDomains() {
      const query = this.domainQuery.toLowerCase();
      return query ? this.pickable.filter((d) => d.host.includes(query)) : this.pickable;
    },
    domain() {
      return this.domains.find((d) => d.id === this.domainId) || null;
    },
    route() {
      if (!this.traffic || !this.traffic.router) return null;
      return this.traffic.router.routes.find((r) => r.path === this.routePath) || null;
    },
    enabledSubs() {
      return this.route ? this.route.sub_params.map((name, index) => ({ name, index })).filter((sub) => sub.name) : [];
    },
    platformOptions() {
      return [{ id: null, title: this.$t("satellites.links.pick_platform") }, ...this.platforms.map((p) => ({ id: p.id, title: `${p.name} · ${p.id}` }))];
    },
    platformOption: {
      get() {
        return this.platformOptions.find((option) => option.id === this.platformId) || this.platformOptions[0];
      },
      set(option) {
        this.platformId = option.id;
      },
    },
    complete() {
      return !!this.route && (this.route.platform.mode !== "param" || !!this.platformId);
    },
    parts() {
      return this.route ? linkParts(this.traffic.domain.host, this.route, this.platformId, this.subValues) : [];
    },
    url() {
      return this.parts.map((part) => part.text).join("");
    },
    check() {
      return checkTraffic(this.traffic, this.route);
    },
    visibleIssues() {
      return this.check.issues;
    },
    dnsKnown() {
      return this.traffic.dns.resolved && this.traffic.dns.records.some((r) => r.satellite);
    },
    verdictTitle() {
      if (this.check.level === "danger") return this.$t("satellites.links.verdict.danger");
      if (this.check.level === "warn") return this.$t("satellites.links.verdict.warn");
      const landings = this.route ? this.route.landings.length : 0;
      return this.$t("satellites.links.verdict.ok", {
        satellites: this.pl("satellites.counts.satellites", this.check.receiving.length),
        landings: this.pl("satellites.counts.landings", landings),
      });
    },
    placementText() {
      const placement = this.traffic.domain.placement;
      if (placement.mode === "all") return this.$t("satellites.links.placement_all");
      const names = [...placement.groups.map((g) => g.name), ...placement.satellites.map((s) => this.satName(s))];
      return names.length ? this.$t("satellites.links.placement_selected", { names: names.join(", ") }) : this.$t("satellites.placement.nowhere");
    },
  },
  watch: {
    routePath: "syncQuery",
    platformId: "syncQuery",
  },
  mounted() {
    Promise.all([satelliteService.getDomains(), satelliteService.getRouterOptions()])
      .then(([domains, options]) => {
        if (domains && domains.status === 403) {
          this.$router.replace({ name: "Dashboard" }).catch(() => {});
          return;
        }
        if (isOk(domains)) this.domains = (domains.data && domains.data.domains) || [];
        else this.showError(domains);
        if (isOk(options)) this.platforms = options.data.platforms || [];
        this.restoreFromQuery();
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    statusDot(status) {
      return { online: "_ok", offline: "_danger" }[status] || "";
    },
    share(route) {
      return route.landings.length ? Math.round((100 / route.landings.length) * 10) / 10 : 0;
    },
    restoreFromQuery() {
      const query = this.$route.query;
      const platform = Number(query.platform);
      if (Number.isInteger(platform) && platform > 0) this.platformId = platform;
      let id = query.domain;
      if (!this.domains.some((d) => d.id === id)) {
        // The only domain of the router, or the first one with a router
        id = this.pickable.length === 1 ? this.pickable[0].id : (this.pickable.find((d) => d.router) || {}).id;
      }
      if (id) this.pickDomain(id, query.path || null);
    },
    pickDomain(id, path = null) {
      if (id === this.domainId && !path) return;
      this.domainId = id;
      this.routePath = path;
      this.domainQuery = "";
      this.loadTraffic();
      this.syncQuery();
    },
    pickRoute(path) {
      this.routePath = path;
    },
    clearScope() {
      this.$router.replace({ query: { ...this.$route.query, router: undefined } }).catch(() => {});
    },
    loadTraffic() {
      if (!this.domainId) return;
      const id = this.domainId;
      this.trafficLoading = true;
      satelliteService
        .getDomainTraffic(id)
        .then((res) => {
          if (id !== this.domainId) return;
          if (isOk(res)) {
            this.traffic = res.data;
            const routes = (res.data.router && res.data.router.routes) || [];
            if (!routes.some((r) => r.path === this.routePath)) this.routePath = routes.length ? routes[0].path : null;
          } else {
            this.traffic = null;
            this.showError(res);
          }
        })
        .finally(() => {
          this.trafficLoading = false;
        });
    },
    syncQuery() {
      const query = { ...this.$route.query, domain: this.domainId || undefined, path: this.routePath || undefined, platform: this.platformId || undefined };
      const same = Object.keys({ ...query, ...this.$route.query }).every((key) => String(query[key] || "") === String(this.$route.query[key] || ""));
      if (!same) this.$router.replace({ query }).catch(() => {});
    },
    copy() {
      if (!this.complete) return;
      navigator.clipboard.writeText(this.url).then(() => {
        this.copied = true;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => (this.copied = false), 1800);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.links-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.links-builder {
  padding: 24px 28px;
}

.links-step {
  display: flex;
  gap: 14px;
  padding: 16px 0;

  & + & {
    border-top: 1px solid var(--sat-border);
  }
}

.links-step__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: var(--sat-accent);
  background: var(--sat-accent-soft);
}

.links-step__body {
  flex: 1;
  min-width: 0;
}

.links-domain-popover {
  display: block;
  width: 100%;
  max-width: 460px;
}

.links-select {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  text-align: left;
  color: var(--sat-text);
  background: var(--sat-input);
  cursor: pointer;

  &:hover {
    border-color: var(--sat-border);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-color: var(--sat-accent);
  }
}

.links-select__main {
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.links-select__meta {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--sat-muted);
}

.links-select__placeholder {
  color: var(--sat-muted);
}

.links-select__chevron {
  margin-left: auto;
  color: var(--sat-muted);
}

.links-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 2px 6px;
  padding: 0 10px;
  border-radius: 8px;
  color: var(--sat-muted);
  background: var(--sat-input);

  input {
    flex: 1;
    height: 36px;
    padding: 0;
    border: 0 !important;
    background: transparent !important;
  }
}

.links-option__host {
  min-width: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.links-option__warn {
  color: var(--sat-warn) !important;
}

.links-option__empty {
  padding: 8px 10px;
  font-size: 13px;
  color: var(--sat-muted);
}

.links-scope {
  margin-top: 8px;

  button {
    display: inline-flex;
    padding: 0;
    margin-left: 2px;
    border: 0;
    color: inherit;
    background: transparent;
    cursor: pointer;
  }
}

.links-routes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.links-route {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 96px;
  padding: 8px 14px;
  border: 1px solid var(--sat-border);
  border-radius: 10px;
  color: var(--sat-text);
  background: var(--sat-card);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--sat-accent);
  }

  &._active {
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);

    .sat-mono {
      color: var(--sat-accent);
      font-weight: 700;
    }
  }

  &:focus {
    outline: none;
  }
}

.links-route__meta {
  font-size: 11px;
  color: var(--sat-muted);
}

.links-platform {
  max-width: 460px;
}

.links-fixed {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  background: var(--sat-surface);

  .sat-muted {
    font-weight: 400;
    font-size: 12px;
  }
}

.links-optional {
  font-weight: 400;
  font-size: 12px;
}

.links-subs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.links-sub {
  margin: 0;

  input {
    width: 100%;
  }
}

.links-sub__label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--sat-text-2);

  .sat-mono {
    font-weight: 400;
    color: var(--sat-muted);
  }
}

.links-result {
  margin-top: 8px;
  padding: 18px;
  border-radius: 14px;
  background: var(--sat-code);
  border: 1px solid var(--sat-border);
}

.links-result__url {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-all;
  color: var(--sat-text);

  ._base {
    font-weight: 600;
  }

  ._path {
    color: var(--sat-accent);
    font-weight: 700;
  }

  ._sep,
  ._key {
    color: var(--sat-muted);
  }

  ._value {
    font-weight: 600;
  }

  ._placeholder {
    padding: 0 6px;
    border-radius: 4px;
    color: var(--sat-warn);
    background: var(--sat-warn-soft);
  }
}

.links-result__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;

  .btn {
    height: 42px;
  }

  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.links-copy {
  min-width: 170px;
  justify-content: center;
}

.links-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.links-verdict {
  position: relative;
  display: flex;
  gap: 14px;
  padding: 18px 48px 18px 20px;
  border-radius: 16px;

  &._ok {
    color: var(--sat-ok);
    background: var(--sat-ok-soft);
  }

  &._warn {
    color: var(--sat-warn);
    background: var(--sat-warn-soft);
  }

  &._danger {
    color: var(--sat-danger);
    background: var(--sat-danger-soft);
  }
}

.links-verdict__icon {
  margin-top: 1px;
}

.links-verdict__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.links-verdict__issues {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sat-text-2);

  li + li {
    margin-top: 4px;
  }

  ._danger {
    color: var(--sat-danger);
  }

  ._warn {
    color: var(--sat-warn);
  }
}

.links-verdict__refresh {
  position: absolute;
  top: 12px;
  right: 10px;
}

.links-side__title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: var(--sat-text);
}

.links-side__hint {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--sat-muted);
}

.links-landing + .links-landing {
  margin-top: 10px;
}

.links-landing__row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 14px;
}

.links-landing__name {
  font-weight: 600;
}

.links-landing__share {
  color: var(--sat-muted);
}

.links-landing__bar {
  height: 6px;
  margin-top: 5px;
  border-radius: 3px;
  background: var(--sat-surface);

  span {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--sat-accent);
    transition: width 0.3s;
  }
}

.links-sat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--sat-border);

  &:last-of-type {
    border-bottom: 0;
  }

  &._idle {
    opacity: 0.55;
  }
}

.links-sat__name {
  min-width: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--sat-text);

  &:hover {
    color: var(--sat-accent);
  }
}

.links-sat__ip {
  margin-left: auto;
  color: var(--sat-muted);
}

.links-dns {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 14px;
}

.links-dns__ok {
  font-weight: 600;
  color: var(--sat-ok);
}

.links-dns__bad {
  font-weight: 600;
  color: var(--sat-danger);
}

.links-ssl {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 13px;
  color: var(--sat-muted);

  &._ok {
    color: var(--sat-ok);
  }
}
</style>
